import {useState, useRef, useEffect, useCallback, useMemo} from 'react'

import Style from './style'

import {
	ROLE_FRANCHISE_MANAGER,
	ROLE_STORE_MANAGER,
} from 'shared/constant/lookup/role'
import MainHeader from 'widget/MainHeader'
import useRoleCookie from 'shared/model/useRoleCookie'
import CStyle from 'shared/style/commonStyle'
import UploadParticipantList from './ui/UploadParticipantList'
import useGetEventTemplate from 'entity/event/useGetEventTemplate'
import usePostEventTemplate from 'entity/event/usePostEventTemplate'
import useAlertModalAtom from 'shared/model/useAlertModalAtom'

const EventParticipantTemplate = () => {
	const [userRole] = useRoleCookie()
	const [, setAlert] = useAlertModalAtom()

	const [shouldDownload, setShouldDownload] = useState(false)
	const [shouldReloadList, setShouldReloadList] = useState(0)
	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false)
	const [isUploadingFile, setIsUploadingFile] = useState(false)

	const fileInputRef = useRef(null)
	
	const [templateResult, resetResult] = useGetEventTemplate(shouldDownload)
	const [uploadResult, uploadFile] = usePostEventTemplate()

	const handleTemplateDownload = useCallback(() => {
		setIsDownloadingTemplate(true)
		setShouldDownload(true)
	}, [])

	const handleFileUpload = useCallback(() => {
		fileInputRef.current?.click()
	}, [])

	const btnList = useMemo(() => {
		const buttons = []

		if (![ROLE_STORE_MANAGER, ROLE_FRANCHISE_MANAGER].includes(userRole)) {
			buttons.push({
				label: '템플릿 다운로드',
				clickEvent: handleTemplateDownload,
				loading: isDownloadingTemplate
			})
			buttons.push({
				label: '참가자 목록 업로드',
				clickEvent: handleFileUpload,
				loading: isUploadingFile
			})
		}

		return buttons
	}, [userRole, handleTemplateDownload, handleFileUpload, isDownloadingTemplate, isUploadingFile])

	useEffect(() => {
    if (shouldDownload) {
			setShouldDownload(false)
    }
	}, [shouldDownload])

	useEffect(() => {
		if (templateResult?.statusCode === 200 && templateResult?.data) {
			try {
				const blob = templateResult.data;
				
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = '주형.xlsx';
				
				document.body.appendChild(a);
				a.click();
				
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);

				resetResult()
			} catch (error) {
				setAlert('파일 다운로드 중 오류가 발생했습니다.');
			} finally {
				setIsDownloadingTemplate(false)
			}
		} else if (templateResult?.statusCode && templateResult?.statusCode !== 200) {
				setIsDownloadingTemplate(false)
				setAlert(templateResult?.data?.message || '템플릿 다운로드에 실패했습니다.')
				resetResult()
		}
	}, [templateResult, resetResult])

	const handleFileChange = (event) => {
		const file = event.target.files[0]
		if (!file) return

		const maxSize = 10 * 1024 * 1024
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

    if (fileExtension !== '.xlsx' || file.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        setAlert('Excel(.xlsx) 파일만 업로드할 수 있습니다.')
				fileInputRef.current.value = ''
        return
    }

		if (file.size > maxSize) {
			setAlert('파일 크기가 허용된 크기(10MB)를 초과했습니다.\n더 작은 파일을 업로드해 주세요.')
			fileInputRef.current.value = ''
			return
		}

		setIsUploadingFile(true)
		uploadFile(file)
	}

	useEffect(() => {
		if (uploadResult?.statusCode === 200) {
			setAlert('업로드가 완료되었습니다.');
			setShouldReloadList(prev => prev + 1);

			if (fileInputRef.current) {
					fileInputRef.current.value = ''
			}
			setIsUploadingFile(false)
		} else if (uploadResult?.statusCode && uploadResult?.statusCode !== 200) {
			setAlert(uploadResult?.data?.message || '업로드 중 오류가 발생했습니다.');
			setIsUploadingFile(false)
		}
	}, [uploadResult])

	return (
		<>
			<Style.Input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
			/>

			<Style.Wrapper>
				<MainHeader btnList={btnList} mobileLayout='default' />

				<div>
					<CStyle.GuideDiv>
						<p>.xlsx 파일만 지원됩니다(파일당 최대 크기: 10MB)</p>
						<ul>
							<li>"템플릿 다운로드"를 클릭하여 최신 Excel 템플릿을 받으세요.</li> 
							<li>템플릿에 참가자 정보를 입력하고 파일을 저장하세요.</li>
							<li>"참가자 목록 업로드"를 클릭하여 작성된 파일을 업로드하세요.</li>
							<li>오류가 있는 경우 메시지를 검토하고 파일을 수정한 후 다시 업로드하세요.</li>
						</ul>
					</CStyle.GuideDiv>
				</div>

				<UploadParticipantList reloadTrigger={shouldReloadList} />
			</Style.Wrapper>
		</>
	)
}

export default EventParticipantTemplate
